// Auth behind a tiny provider interface (§13.2): `discord` for production,
// `local` (email/password against local Supabase) for the harness. Both route
// through the same /auth/callback code path so pendingInvite handling is always
// exercised.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { getSupabase, isSupabaseConfigured } from "../lib/supabase.ts";
import { AUTH_PROVIDER, IS_TEST } from "../lib/env.ts";

export interface DevUser {
  key: "dave" | "sarah" | "new";
  label: string;
  email: string;
  password: string;
}

// Deterministic local credentials, mirrored by the seed script.
export const DEV_USERS: DevUser[] = [
  {
    key: "dave",
    label: "Sign in as Akhil",
    email: "dave@twoshot.test",
    password: "password123",
  },
  {
    key: "sarah",
    label: "Sign in as Liban",
    email: "sarah@twoshot.test",
    password: "password123",
  },
  {
    key: "new",
    label: "Sign in as New User",
    email: "newuser@twoshot.test",
    password: "password123",
  },
];

interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
  configured: boolean;
  /** Signed in via Supabase anonymous sign-in (guest mode). */
  isAnonymous: boolean;
  provider: "local" | "discord";
  signInWithOAuth: (
    provider: OAuthProvider,
    redirectPath?: string,
  ) => Promise<void>;
  signInWithDiscord: (redirectPath?: string) => Promise<void>;
  /** Guest mode: a session with no identity. Requires "Allow anonymous
   *  sign-ins" in the Supabase dashboard; throws its raw error otherwise. */
  signInAnonymously: () => Promise<void>;
  /** Attach an OAuth identity to the current (guest) user — same user_id, so
   *  the profile and blends survive. Requires "Allow manual linking". */
  linkIdentity: (
    provider: OAuthProvider,
    redirectPath?: string,
  ) => Promise<void>;
  signInLocal: (email: string, password: string) => Promise<void>;
  signInAsDev: (key: DevUser["key"]) => Promise<void>;
  signOut: () => Promise<void>;
}

/** OAuth providers configured in Supabase (Auth → Providers). */
export type OAuthProvider = "discord" | "spotify" | "google";

const AuthContext = createContext<AuthState | null>(null);

export const PENDING_INVITE_KEY = "pendingInvite";

export function setPendingInvite(slug: string) {
  try {
    localStorage.setItem(PENDING_INVITE_KEY, slug);
  } catch {
    /* ignore */
  }
}
export function consumePendingInvite(): string | null {
  try {
    const v = localStorage.getItem(PENDING_INVITE_KEY);
    if (v) localStorage.removeItem(PENDING_INVITE_KEY);
    return v;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const configured = isSupabaseConfigured();

  useEffect(() => {
    const sb = getSupabase();
    if (!sb) {
      setLoading(false);
      return;
    }
    sb.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = sb.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      configured,
      isAnonymous: session?.user?.is_anonymous === true,
      provider: AUTH_PROVIDER,
      async signInWithOAuth(provider, redirectPath = "/auth/callback") {
        const sb = getSupabase();
        if (!sb) throw new Error("Auth is not configured.");
        await sb.auth.signInWithOAuth({
          provider,
          options: { redirectTo: window.location.origin + redirectPath },
        });
      },
      async signInWithDiscord(redirectPath = "/auth/callback") {
        await value.signInWithOAuth("discord", redirectPath);
      },
      async signInAnonymously() {
        const sb = getSupabase();
        if (!sb) throw new Error("Auth is not configured.");
        const { error } = await sb.auth.signInAnonymously();
        if (error) throw error;
      },
      async linkIdentity(provider, redirectPath = "/auth/callback") {
        const sb = getSupabase();
        if (!sb) throw new Error("Auth is not configured.");
        const { error } = await sb.auth.linkIdentity({
          provider,
          options: { redirectTo: window.location.origin + redirectPath },
        });
        if (error) throw error;
      },
      async signInLocal(email, password) {
        const sb = getSupabase();
        if (!sb) throw new Error("Auth is not configured.");
        const { error } = await sb.auth.signInWithPassword({ email, password });
        if (error) throw error;
      },
      async signInAsDev(key) {
        const u = DEV_USERS.find((d) => d.key === key);
        if (!u) throw new Error("Unknown dev user");
        await value.signInLocal(u.email, u.password);
      },
      async signOut() {
        const sb = getSupabase();
        if (sb) await sb.auth.signOut();
        setSession(null);
      },
    }),
    [session, loading, configured],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Dev login buttons are shown only in test/local mode (§13.2). */
export const SHOW_DEV_LOGINS = IS_TEST || AUTH_PROVIDER === "local";
